import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import api from "@/lib/api";
import { Overline, FadeIn, StatusBadge } from "@/components/site/Shared";

const categories = ["All", "Residential", "Commercial", "Industrial", "Roads & Infrastructure", "Renovation"];

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  const visible = filter === "All" ? projects : projects.filter((p) => p.category === filter);

  return (
    <div data-testid="projects-page">
      <section className="py-24 sm:py-32 border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <Overline className="mb-5">Portfolio</Overline>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter text-white leading-[0.95] max-w-4xl">
              Built Proof.<br /><span className="text-ember">Not Promises.</span>
            </h1>
            <p className="text-silver max-w-2xl mt-8 leading-relaxed">
              A selection from 250+ delivered and ongoing projects — each with verifiable milestones, budgets held, and clients who will take your call.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-wrap gap-3 mb-14" data-testid="project-filters">
            {categories.map((c) => (
              <button key={c} onClick={() => setFilter(c)} data-testid={`filter-${c.toLowerCase().replace(/[\s&]+/g, "-")}`}
                className={`text-xs uppercase tracking-[0.15em] font-bold px-5 py-3 border transition-colors duration-200 ${filter === c ? "bg-ember border-ember text-white" : "border-line text-silver hover:border-ember hover:text-white"}`}>
                {c}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {visible.map((p, i) => (
              <FadeIn key={p.slug} delay={(i % 2) * 0.08}>
                <Link to={`/projects/${p.slug}`} data-testid={`project-card-${p.slug}`}
                  className="group block border border-line bg-graphite lift h-full">
                  <div className="img-zoom overflow-hidden h-72 relative">
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
                    <div className="absolute top-4 left-4"><StatusBadge status={p.status} /></div>
                  </div>
                  <div className="p-8">
                    <p className="text-ember text-xs uppercase tracking-[0.2em] font-bold">{p.category}</p>
                    <h3 className="font-heading text-2xl text-white uppercase tracking-tight mt-2 group-hover:text-ember transition-colors duration-200">{p.title}</h3>
                    <p className="text-silver text-sm mt-2 flex items-center gap-2"><MapPin size={14} className="text-ember" /> {p.location}</p>
                    <p className="text-silver text-sm mt-4 leading-relaxed line-clamp-2">{p.summary}</p>
                    <div className="mt-6 pt-6 border-t border-line grid grid-cols-3 gap-4 text-xs">
                      <div><p className="text-silver uppercase tracking-wider">Area</p><p className="text-white mt-1 font-medium">{p.area}</p></div>
                      <div><p className="text-silver uppercase tracking-wider">Duration</p><p className="text-white mt-1 font-medium">{p.duration}</p></div>
                      <div><p className="text-silver uppercase tracking-wider">Progress</p><p className="text-ember mt-1 font-bold">{p.completion_pct}%</p></div>
                    </div>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
          {visible.length === 0 && <p className="text-silver text-center py-20">No projects in this category yet.</p>}
        </div>
      </section>
    </div>
  );
}
